// ============================================================================
// Fuel Types
// ============================================================================

export type FuelType = 'E10' | 'E5' | 'B7_STANDARD' | 'B7_PREMIUM' | 'B10' | 'HVO';

export const FUEL_TYPES: FuelType[] = ['E10', 'E5', 'B7_STANDARD', 'B7_PREMIUM', 'B10', 'HVO'];

export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export const DAYS_OF_WEEK: DayOfWeek[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

// ============================================================================
// Price & Opening Hours
// ============================================================================

export interface PriceInfo {
  price: number; // in pence, e.g. 145.9
  updated: string; // ISO timestamp
}

export interface OpeningTime {
  open: string; // "06:00"
  close: string; // "22:00"
  is24Hours: boolean;
}

// ============================================================================
// Station
// ============================================================================

export interface StationAddress {
  line1: string;
  line2: string | null;
  city: string;
  county: string;
  postcode: string;
}

export interface StationLocation {
  lat: number;
  lng: number;
}

export interface Station {
  id: string;
  name: string;
  brand: string;
  address: StationAddress;
  location: StationLocation;
  // Convenience properties - flattened from location
  latitude: number;
  longitude: number;
  distance: number; // in miles
  prices: Record<FuelType, PriceInfo | null>;
  amenities: string[];
  openingHours: Record<DayOfWeek, OpeningTime>;
  bankHoliday: OpeningTime;
  isMotorway: boolean;
  isSupermarket: boolean;
  isOpen: boolean;
  temporaryClosure: boolean;
  permanentClosure: boolean;
}

// ============================================================================
// API Responses
// ============================================================================

export interface StationsResponseMeta {
  total: number;
  radius: number;
  centre: StationLocation;
  dataTimestamp: string;
  cacheAge: number;
}

export interface StationsResponse {
  stations: Station[];
  meta: StationsResponseMeta;
}

export interface PriceHistoryEntry {
  timestamp: string;
  prices: Partial<Record<FuelType, number>>;
}

export interface PriceHistoryResponse {
  stationId: string;
  stationName: string;
  history: PriceHistoryEntry[];
}

export interface ApiError {
  error: string;
  message?: string;
}

// ============================================================================
// Search State
// ============================================================================

export interface SearchLocation {
  latitude: number;
  longitude: number;
}

export interface SearchState {
  location: SearchLocation | null;
  locationName: string | null; // "SW1A 1AA" or "Westminster, London"
  hasSearched: boolean;
}

export interface SearchContextValue extends SearchState {
  setLocation: (location: SearchLocation | null, name: string | null) => void;
  clearSearch: () => void;
}

// ============================================================================
// Filters State
// ============================================================================

export interface FiltersState {
  selectedFuelTypes: FuelType[];
  selectedBrands: string[];
  selectedAmenities: string[];
  primaryFuelType: FuelType; // The one shown on map markers
  radius: number; // miles - moved here from search
}

export interface FiltersContextValue extends FiltersState {
  // Fuel type actions
  toggleFuelType: (fuelType: FuelType) => void;
  setPrimaryFuelType: (fuelType: FuelType) => void;
  // Radius actions
  setRadius: (miles: number) => void;
  // Brand actions
  toggleBrand: (brand: string) => void;
  selectAllBrands: (brands: string[]) => void;
  clearBrands: () => void;
  // Amenity actions
  toggleAmenity: (amenity: string) => void;
  // Reset
  resetFilters: () => void;
  // Available brands from current results (for dynamic filtering)
  availableBrands: string[];
  setAvailableBrands: (brands: string[]) => void;
}

// ============================================================================
// Station Selection State
// ============================================================================

export interface StationContextValue {
  selectedStation: Station | null;
  selectStation: (station: Station | null) => void;
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
}

// ============================================================================
// Map Types
// ============================================================================

export interface MapViewState {
  longitude: number;
  latitude: number;
  zoom: number;
}

export interface ClusterProperties {
  cluster: boolean;
  cluster_id: number;
  point_count: number;
  point_count_abbreviated: string;
}

// ============================================================================
// Geolocation
// ============================================================================

export interface GeolocationState {
  location: SearchLocation | null;
  error: string | null;
  loading: boolean;
}

// ============================================================================
// Geocoding
// ============================================================================

export interface GeocodingResult {
  name: string;
  location: SearchLocation;
  fullAddress: string;
}
